/**
 * Hook pour formater les dates dans la timezone du brand.
 *
 * Wrapper autour des helpers purs de @bill/ui : il bind automatiquement
 * la timezone du brand courant pour eviter de la passer a chaque appel.
 *
 * @example
 * const { formatDate, formatDateTime, formatRelative } = useFormatDate();
 * formatDate(receipt.createdAt)        // "15 mars 2024"
 * formatDateTime(receipt.createdAt)    // "15 mars 2024 a 14:30"
 * formatRelative(notification.createdAt)
 */

import { useCallback, useMemo } from 'react';
import {
  formatDate as fmtDate,
  formatDateShort as fmtDateShort,
  formatDateTime as fmtDateTime,
  formatDateTimeShort as fmtDateTimeShort,
  formatTime as fmtTime,
  formatRelative as fmtRelative,
  type DateInput,
} from '@bill/ui';
import { useBrandTimezone } from './useBrandContext';

interface FormatOptions {
  /** Timezone override (default: brand timezone) */
  timezone?: string;
  /** Custom date-fns format string */
  format?: string;
}

/**
 * Hook pour formater les dates dans la timezone du brand.
 */
export function useFormatDate() {
  const brandTimezone = useBrandTimezone();

  const formatDate = useCallback(
    (date: DateInput, options?: FormatOptions) =>
      fmtDate(date, options?.timezone ?? brandTimezone, options?.format),
    [brandTimezone]
  );

  const formatDateShort = useCallback(
    (date: DateInput, options?: FormatOptions) =>
      options?.format
        ? fmtDate(date, options.timezone ?? brandTimezone, options.format)
        : fmtDateShort(date, options?.timezone ?? brandTimezone),
    [brandTimezone]
  );

  const formatDateTime = useCallback(
    (date: DateInput, options?: FormatOptions) =>
      options?.format
        ? fmtDate(date, options.timezone ?? brandTimezone, options.format)
        : fmtDateTime(date, options?.timezone ?? brandTimezone),
    [brandTimezone]
  );

  const formatDateTimeShort = useCallback(
    (date: DateInput, options?: FormatOptions) =>
      options?.format
        ? fmtDate(date, options.timezone ?? brandTimezone, options.format)
        : fmtDateTimeShort(date, options?.timezone ?? brandTimezone),
    [brandTimezone]
  );

  const formatTime = useCallback(
    (date: DateInput, options?: FormatOptions) =>
      options?.format
        ? fmtDate(date, options.timezone ?? brandTimezone, options.format)
        : fmtTime(date, options?.timezone ?? brandTimezone),
    [brandTimezone]
  );

  const formatRelative = useCallback(
    (date: DateInput, options?: FormatOptions) =>
      fmtRelative(date, options?.timezone ?? brandTimezone),
    [brandTimezone]
  );

  const formatDateWithOptions = useCallback(
    (date: DateInput, options?: FormatOptions & { includeTime?: boolean; relative?: boolean }) => {
      if (options?.relative) return formatRelative(date, options);
      if (options?.includeTime) return formatDateTime(date, options);
      return formatDate(date, options);
    },
    [formatDate, formatDateTime, formatRelative]
  );

  return useMemo(
    () => ({
      formatDate,
      formatDateShort,
      formatDateTime,
      formatDateTimeShort,
      formatTime,
      formatRelative,
      formatDateWithOptions,
      timezone: brandTimezone,
    }),
    [
      formatDate,
      formatDateShort,
      formatDateTime,
      formatDateTimeShort,
      formatTime,
      formatRelative,
      formatDateWithOptions,
      brandTimezone,
    ]
  );
}

